import { useState } from "react"
import { useNavigate, NavLink, Link, useLocation } from "react-router"
import { api } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import { useNotifications } from "../../contexts/NotificationsContext"
import AccountSelectorModal from "../account/AccountSelectorModal"
import OrgSelectorModal from "./OrgSelectorModal"
import appName from "../../lib/appName"
import { NAV_ITEMS } from "../../lib/navItems"
import "../../styles/sidebar.scss"

type Props = {
  isOpen: boolean
  onClose: () => void
}

const Sidebar = ({ isOpen, onClose }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedAccount, selectedAccountIds, organizations, selectedOrganizationId, singleAccountOnly } = useSelectedAccount()
  const { unreadCount } = useNotifications()
  const [showAccountModal, setShowAccountModal] = useState(false)

  const isSettingsPage = location.pathname === "/settings"
  const selectedOrg = organizations.find(o => o.id === selectedOrganizationId)

  const accountSelectorLabel = () => {
    if (isSettingsPage) return selectedOrg?.name ?? "組織を選択"
    if (selectedAccountIds.length === 0) return "アカウントなし"
    if (singleAccountOnly || selectedAccountIds.length === 1) return selectedAccount?.username ? `@${selectedAccount.username}` : "アカウントを選択"
    return `${selectedAccountIds.length}アカウントを選択中`
  }

  const visibleItems = selectedAccountIds.length > 1
    ? NAV_ITEMS.filter(item => !item.singleOnly)
    : NAV_ITEMS

  const handleLogout = async () => {
    await api.delete("/users/sign_out").catch(() => {})
    navigate("/login")
  }

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <nav className={`sidebar${isOpen ? " sidebar--open" : ""}`}>
        <Link to="/" className="sidebar-brand" onClick={onClose}>
          <img src="/logo.png" alt={appName()} className="sidebar-brand__logo" />
        </Link>

        <button
          className="sidebar-account-selector"
          onClick={() => setShowAccountModal(true)}
          aria-label={isSettingsPage ? "組織を選択" : "アカウントを選択"}
        >
          <div className="sidebar-account-selector__avatar">
            {isSettingsPage ? (
              <i className="bi bi-building" />
            ) : (singleAccountOnly || selectedAccountIds.length === 1) && selectedAccount?.avatar_url ? (
              <img src={selectedAccount.avatar_url} alt={selectedAccount.username} />
            ) : (
              <i className="bi bi-instagram" />
            )}
          </div>
          <span className="sidebar-account-selector__name">{accountSelectorLabel()}</span>
          <i className="bi bi-chevron-down sidebar-account-selector__chevron" />
        </button>

        <ul className="sidebar-nav">
          {visibleItems.map(({ path, icon, label, end, feature }) => {
            const locked = feature != null && selectedAccount != null && !selectedAccount.features[feature]
            return (
              <li key={path}>
                <NavLink
                  to={path}
                  end={end}
                  className={({ isActive }) =>
                    `sidebar-nav__item${isActive ? " sidebar-nav__item--active" : ""}`
                  }
                  onClick={onClose}
                >
                  <i className={`bi ${icon} sidebar-nav__icon`} />
                  <span className="sidebar-nav__label">{label}</span>
                  {locked && <i className="bi bi-lock-fill sidebar-nav__lock" title="プロプラン限定" />}
                </NavLink>
              </li>
            )
          })}
        </ul>

        <div className="sidebar-footer">
          <NavLink
            to="/notifications"
            className={({ isActive }) =>
              `sidebar-footer-nav__item${isActive ? " sidebar-nav__item--active" : ""}`
            }
            onClick={onClose}
          >
            <i className="bi bi-bell sidebar-nav__icon" />
            <span className="sidebar-nav__label">お知らせ</span>
            {unreadCount > 0 && (
              <span className="sidebar-unread-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
            )}
          </NavLink>

          <NavLink
            to="/help"
            className={({ isActive }) =>
              `sidebar-footer-nav__item${isActive ? " sidebar-nav__item--active" : ""}`
            }
            onClick={onClose}
          >
            <i className="bi bi-question-circle sidebar-nav__icon" />
            <span className="sidebar-nav__label">ヘルプ</span>
          </NavLink>

          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `sidebar-footer-nav__item${isActive ? " sidebar-nav__item--active" : ""}`
            }
            onClick={onClose}
          >
            <i className="bi bi-gear sidebar-nav__icon" />
            <span className="sidebar-nav__label">設定</span>
          </NavLink>

          <button className="sidebar-logout" onClick={handleLogout}>
            <i className="bi bi-box-arrow-left sidebar-nav__icon" />
            <span className="sidebar-nav__label">ログアウト</span>
          </button>
        </div>
      </nav>
      {showAccountModal && isSettingsPage
        ? <OrgSelectorModal onClose={() => setShowAccountModal(false)} />
        : showAccountModal && <AccountSelectorModal onClose={() => setShowAccountModal(false)} />
      }
    </>
  )
}

export default Sidebar
